import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link,
    Typography,
    Button
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../Common/ConfirmDialog";
import ClearIcon from "@mui/icons-material/Clear";

const RESULT_FIELDS = [
    { key: "bABFCalibrated",    label: "ABF Calibrated" },
    { key: "sMwCalParameterCount", label: "Param Count" },
    { key: "P1",                label: "Parameter 1" },
    { key: "P2",                label: "Parameter 2" },
    { key: "P3",                label: "Parameter 3" },
    { key: "P4",                label: "Parameter 4" },
    { key: "P5",                label: "Parameter 5" },
    { key: "ShapeFactor",       label: "Shape Factor" },
    { key: "FitFactor",         label: "Fit Factor" },
    { key: "SignalMax",         label: "Signal Max" },
    { key: "ConcMax",           label: "Conc Max" },
    { key: "ConditionCode",     label: "Condition Code" },
    { key: "Error",             label: "Error" }
];

export default function MicroWellHistory({ resultDataMap, rows, parameters, setResultDataMap }) {
    const navigate = useNavigate();
    const [selectedRun, setSelectedRun] = useState(null);
    const [openClearDialog, setOpenClearDialog] = useState(false);

    const historyData = Object.values(resultDataMap || {});

    useEffect(() => {
        const runs = Object.keys(resultDataMap || {});
        if (runs.length > 0) {
            setSelectedRun(Number(runs[runs.length - 1]));
        }
    }, [resultDataMap]);

    const selectedEntry = resultDataMap?.[selectedRun];
    const selectedResult = selectedEntry?.result;
    const selectedDescription = selectedEntry?.description;

    const handleClearConfirm = () => {
        setResultDataMap({});
        sessionStorage.removeItem("microwellCalibrationHistory");
        setOpenClearDialog(false);
    };

    const handlePredictNavigate = (run) => {
        navigate("/MicroWell", {
            state: { openPredictionTab: true, runId: run }
        });
    };

    return (
        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", md: "row" } }}>

            {/* Result detail panel */}
            <Paper sx={{ flex: 1, p: 2, minHeight: 300 }}>
                <Typography variant="h6" gutterBottom>
                    Result {selectedRun ? `(${selectedDescription})` : ""}
                </Typography>

                {selectedResult ? (
                    <Table size="small">
                        <TableBody>
                            {RESULT_FIELDS.map((field) => (
                                <TableRow key={field.key}>
                                    <TableCell sx={{ fontWeight: 600, width: 160 }}>{field.label}</TableCell>
                                    <TableCell>{selectedResult[field.key] ?? ""}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <Typography color="text.secondary" variant="body2">
                        No run selected. Run Calibrate to see results here.
                    </Typography>
                )}

                {selectedEntry && (
                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => handlePredictNavigate(selectedRun)}
                    >
                        Go to Prediction ?
                    </Button>
                )}
            </Paper>

            {/* Run history table */}
            <Paper sx={{ flex: 2, p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6">Calibration History</Typography>
                    {historyData.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<ClearIcon />}
                            onClick={() => setOpenClearDialog(true)}
                        >
                            Clear
                        </Button>
                    )}
                </Box>

                {historyData.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                        No calibration runs yet.
                    </Typography>
                ) : (
                    <TableContainer sx={{ maxHeight: 400, overflowY: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>ABF Cal</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>P1</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>P2</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Shape</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Fit</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Report</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Predict</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyData.map((entry) => (
                                    <TableRow
                                        key={entry.run}
                                        hover
                                        selected={selectedRun === entry.run}
                                        onClick={() => setSelectedRun(entry.run)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <TableCell>{entry.run}</TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                        <TableCell>{entry.result?.bABFCalibrated}</TableCell>
                                        <TableCell>{entry.result?.P1}</TableCell>
                                        <TableCell>{entry.result?.P2}</TableCell>
                                        <TableCell>{entry.result?.ShapeFactor}</TableCell>
                                        <TableCell>{entry.result?.FitFactor}</TableCell>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate("/MicroWellCalibrationReport", {
                                                        state: { runData: entry, rows, parameters }
                                                    });
                                                }}
                                            >
                                                Launch
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePredictNavigate(entry.run);
                                                }}
                                            >
                                                Launch
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <ConfirmDialog
                open={openClearDialog}
                title="Clear Calibration History"
                message="This will clear all calibration history. Continue?"
                onConfirm={handleClearConfirm}
                onCancel={() => setOpenClearDialog(false)}
            />
        </Box>
    );
}
